import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TicketReportColumnDto, TicketReportColumnTemplateDto, TicketReportColumnValueTypeModeEnum, TicketReportDto, TicketReportPreviewRowDto, WorkflowDto } from '../../../../service/dto';
import { HttpWorkflowService } from '../../../../service/http-workflow.service';
import { HttpTicketReportService } from '../../../../service/http-ticket-report.service';
import { NzCustomColumn } from 'ng-zorro-antd/table';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { TicketReportFormSimpleModalComponent } from '../ticket-report-form-simple-modal/ticket-report-form-simple-modal.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TicketReportFormSubColumnModalComponent } from '../ticket-report-form-sub-column-modal/ticket-report-form-sub-column-modal.component';
import { faHome, faTicket, IconDefinition, faUser, faUsers, faLayerGroup, faIcons, faBuilding, faAnglesRight, faKeyboard, faComputerMouse, faMessage, faComment, faDashboard, faMicrochip, faDotCircle } from '@fortawesome/free-solid-svg-icons';

export interface MenuGroup {
	label: string;
	templates: TicketReportColumnTemplateDto[];
	subMenus: MenuSubGroup[];
}
interface MenuSubGroup {
	label: string;
	payload: TicketReportColumnTemplateDto[];
}


@Component({
	selector: 'app-ticket-report-form',
	standalone: false,
	templateUrl: './ticket-report-form.component.html',
	styleUrl: './ticket-report-form.component.scss'
})
export class TicketReportFormComponent implements OnInit {
	static counter = 0;

	form!: FormGroup;
	workflow?: WorkflowDto;
	ticketReport!: TicketReportDto;
	menu: MenuGroup[] = [];
	previewData: TicketReportPreviewRowDto[] = [];
	loading = false;

	constructor(
		private fb: FormBuilder,
		private route: ActivatedRoute,
		private modalService: NzModalService,
		private httpWorkflowService: HttpWorkflowService,
		private httpTicketReportService: HttpTicketReportService) { }

	ngOnInit(): void {

		const workflowId = this.route.snapshot.paramMap.get('workflowId');
		const reportId = this.route.snapshot.paramMap.get('reportId');

		if (workflowId) {
			this.httpWorkflowService.get(+workflowId).subscribe(result => {
				this.init({
					id: TicketReportFormComponent.getNextId(),
					createdAt: new Date(),
					name: '',
					description: '',
					isSummary: false,
					columns: [],
					workflow: result,
					hashCode: 0
				});
			});
		} else if (reportId) {
			this.httpTicketReportService.get(+reportId!).subscribe(result => this.init(result));
		}
	}
	init(input: TicketReportDto) {
		this.workflow = input.workflow;
		this.ticketReport = input;
		this.form = this.fb.group({
			name: [input.name, [Validators.required]],
			description: [input.description, [Validators.required]],
			date: [undefined, (control: any) => {
				const val = control.value;
				if (!val) return { required: true };
				if (val instanceof Date) {
					return isNaN(val.getTime()) ? { invalidDate: true } : null;
				}
				const parsed = new Date(val);
				return isNaN(parsed.getTime()) ? { invalidDate: true } : null;
			}]
		});
		this.menu = this.buildMenu();
	}
	buildMenu(): MenuGroup[] {
		const groups: { [key: string]: { [key: string]: TicketReportColumnTemplateDto[] } } = {};

		for (const t of this.workflow!.reportColumnTemplates) {
			if (!groups[t.group1]) {
				groups[t.group1] = {};
			}
			const subGroupKey = t.group2 || '_default';
			if (!groups[t.group1][subGroupKey]) {
				groups[t.group1][subGroupKey] = [];
			}
			groups[t.group1][subGroupKey].push(t);
		}

		return Object.keys(groups).map(group1 => ({
			label: group1,
			templates: groups[group1]['_default'] || [],
			subMenus: Object.keys(groups[group1])
				.filter(k => k !== '_default')
				.map(group2 => ({
					label: group2,
					payload: groups[group1][group2]
				}))
		}));
	}

	onCreate(): void {
		this.ticketReport.name = this.form.get('name')!.value;
		this.ticketReport.description = this.form.get('description')!.value;
		this.httpTicketReportService.create(this.workflow!.id, this.ticketReport).subscribe(result => {
			this.init(result);
		});
	}
	onSave(): void {
		this.ticketReport.name = this.form.get('name')!.value;
		this.ticketReport.description = this.form.get('description')!.value;
		this.httpTicketReportService.save(this.ticketReport.id, this.ticketReport).subscribe(result => {
			this.init(result);
		});
	}
	addColumn(input: TicketReportColumnTemplateDto) {
		console.log("Adding:", input.modes);
		const column = {
			id: TicketReportFormComponent.getNextId(),
			createdAt: new Date(),
			title: input.name,
			mode: Object.keys(input.modes)[0] as TicketReportColumnValueTypeModeEnum,
			type: input.type,
			value: input.value,
			order: this.ticketReport.columns.length + 1,
			template: input,
			parentId: undefined,
			subColumns: [],
			hashCode: 0
		};
		this.ticketReport.columns!.push(column);
		if (input.type == 'DateDiff' || input.type == 'MinValue' || input.type == 'MaxValue' || input.type == 'Coalesce') {
			this.editColumn(column);
		} else {
			this.reloadPreview();
		}
	}
	editColumn(input: TicketReportColumnDto) {
		let modal!: NzModalRef;
		if (input.type == 'DateDiff' || input.type == 'MinValue' || input.type == 'MaxValue' || input.type == 'Coalesce') {
			modal = this.modalService.create({
				nzTitle: `Edit Column: ${input.template!.group1}: ${input.template!.group2}: <b>${input.template!.name}</b>`,
				nzContent: TicketReportFormSubColumnModalComponent,
				nzWidth: '80%',
				nzFooter: null
			});
			modal.componentInstance!.workflow = this.workflow;
		} else {
			modal = this.modalService.create({
				nzTitle: `Edit Column: ${input.template!.group1??''}: ${input.template!.group2??''}: <b>${input.template!.name}</b>`,
				nzContent: TicketReportFormSimpleModalComponent,
				nzFooter: null
			});
		}
		modal.componentInstance!.report = this.ticketReport;
		modal.componentInstance!.column = input;

		modal.afterClose.subscribe(result => {
			if (result == true) {
				this.reorderColumns();
				this.reloadPreview();
			}
		});
	}
	reloadPreview() {
		if (!this.form.get('date')!.valid) {
			this.previewData = [];
			return;
		}
		if (this.ticketReport.columns.length == 0) {
			this.previewData = [];
			return;
		}
		this.loading = true;
		this.httpTicketReportService.preview(this.workflow!.id, this.form.get('date')!.value, this.ticketReport).subscribe({
			next: previewData => {
				this.previewData = previewData;
				this.loading = false;
			},
			error: err => {
				this.loading = false;
				this.previewData = [];
				console.error('Ticket report preview failed', err);
				this.modalService.error({
					nzTitle: 'Failed to load preview',
					nzContent: err?.message ?? 'An unexpected error occurred while generating the preview.'
				});
			}
		});
	}
	private reorderColumns() {
		let columns = this.ticketReport.columns.sort((i1, i2) => i1.order! - i2.order!);
		columns.forEach((col, idx) => col.order = idx + 1);
		this.ticketReport.columns = [...columns];
	}

	public static getNextId(): number {
		return --this.counter;
	}
	dropColumn(event: CdkDragDrop<TicketReportColumnDto[]>): void {
		console.log('Drop event:', event.previousIndex, '->', event.currentIndex);

		const columns = [...this.ticketReport.columns];
		const movedColumn = columns[event.previousIndex];
		columns.splice(event.previousIndex, 1);
		columns.splice(event.currentIndex, 0, movedColumn);
		columns.forEach((col, idx) => col.order = idx + 1);
		this.ticketReport.columns = columns;
		this.reorderColumns();
		this.reloadPreview();
	}

	getMenuIcon(input: TicketReportColumnTemplateDto){
		switch(input.type){
			case 'Ticket': return faTicket;
			case 'Member': return faUser;
			case 'Dependant': return faUsers;
			case 'Membership': return faLayerGroup;
			case 'Category': return faIcons;
			case 'Employer': return faBuilding;
			case 'Movement': return faAnglesRight;
			case 'Element': return faKeyboard;
			case 'Click': return faComputerMouse;
			case 'Communication': return faMessage;
			case 'Comment': return faComment;
			case 'Report': return faDashboard;
			case 'DateDiff': return faMicrochip;
			case 'MinValue': return faMicrochip;
			case 'MaxValue': return faMicrochip;
			case 'Coalesce': return faMicrochip;
			default: return faDotCircle;
		}
	}
}
